import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { getPublicInvoiceLink, submitPublicInvoice } from '../../api_handlers/invoice';
import { Alert, ICONS, Icon, KIT_CSS, money, shortDate, useStyleSheet } from './kit';
import PayoutFields, {
  EMPTY_PAYOUT,
  PAYOUT_CSS,
  payoutPayload,
  validatePayout,
} from './PayoutFields';

/**
 * The no-sign-in invoice form behind an exception link (IN-1718, Flow 2).
 *
 * Staff create the link for one creator on one campaign — urgent payments,
 * or creators and agencies who will not authenticate — and share it, or open
 * it themselves to fill in on the creator's behalf. It collects the same
 * invoice fields as the signed-in form plus payout details, which are
 * stored on this invoice only and never written to the creator's profile.
 *
 * With no login, nothing else says who filled it in, so the form starts by
 * asking — the creator, an agency, or the internal team — and that identity
 * is stored on the invoice for exception-usage tracking. A signed-in viewer
 * skips the step: their account already says who they are.
 */

const CSS = `
.pub-page { min-height: 100vh; background: var(--canvas); padding: 32px 16px 80px; }
.pub-wrap { max-width: 720px; margin: 0 auto; }
.pub-brand { font-size: 13px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; color: var(--accent-ink); margin: 0 0 18px; }
.pub-hero { background: var(--surface); border: 1px solid var(--line); border-radius: 16px; padding: 22px 24px; margin-bottom: 18px; box-shadow: var(--shadow-sm); }
.pub-hero__kicker { font-size: 11.5px; font-weight: 800; letter-spacing: .1em; text-transform: uppercase; color: var(--ink-4); margin: 0 0 6px; }
.pub-hero__title { font-size: 22px; font-weight: 800; color: var(--ink); margin: 0; letter-spacing: -.01em; }
.pub-hero__meta { font-size: 13.5px; color: var(--ink-3); margin: 6px 0 0; }
.pub-hero__amount { display: flex; gap: 18px; margin-top: 16px; flex-wrap: wrap; }
.pub-hero__stat { font-size: 12px; color: var(--ink-3); }
.pub-hero__stat strong { display: block; font-size: 18px; color: var(--ink); font-weight: 800; }
.pub-card { background: var(--surface); border: 1px solid var(--line); border-radius: 16px; padding: 22px 24px; margin-bottom: 18px; }
.pub-card__title { font-size: 15px; font-weight: 800; color: var(--ink); margin: 0 0 4px; }
.pub-card__sub { font-size: 12.5px; color: var(--ink-3); margin: 0 0 18px; }
.pub-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 16px; }
.pub-full { grid-column: 1 / -1; }
.pub-foot { display: flex; justify-content: flex-end; gap: 10px; }
.pub-done { text-align: center; padding: 40px 24px; }
.pub-who { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 18px; }
.pub-who__opt { font-family: inherit; text-align: left; cursor: pointer; background: var(--surface); border: 1px solid var(--line-2); border-radius: 12px; padding: 12px 14px; transition: border-color .15s ease, background .15s ease; }
.pub-who__opt:hover { border-color: var(--accent); }
.pub-who__opt--on { border-color: var(--accent); background: var(--accent-bg); box-shadow: 0 0 0 1px var(--accent) inset; }
.pub-who__label { display: block; font-size: 14px; font-weight: 800; color: var(--ink); }
.pub-who__hint { display: block; font-size: 12px; color: var(--ink-3); margin-top: 3px; }
.pub-who__err { font-size: 12.5px; color: #B91C1C; margin: -8px 0 14px; }
.pub-signed { font-size: 13.5px; color: var(--ink-2); margin: 0; }
@media (max-width: 560px) { .pub-who { grid-template-columns: 1fr; } }
.pub-done__icon { width: 52px; height: 52px; border-radius: 50%; background: #DCFCE7; color: #15803D; display: grid; place-items: center; margin: 0 auto 16px; }
@media (max-width: 560px) { .pub-grid { grid-template-columns: 1fr; } .pub-hero, .pub-card { padding: 18px; } }
`;

const SUBMITTER_HINTS = {
  creator: 'I am the creator on this campaign.',
  agency: 'I manage this creator at an agency.',
  internal: 'I work at Influencer.in and am filling this in for the creator.',
};

const DEFAULT_SUBMITTER_TYPES = [
  { value: 'creator', label: 'Creator' },
  { value: 'agency', label: 'Agency' },
  { value: 'internal', label: 'Internal team' },
];

const EMPTY_SUBMITTER = {
  submitter_type: '',
  submitter_name: '',
  submitter_agency: '',
  submitter_email: '',
  submitter_phone: '',
  submitter_profile: '',
};

/** Mirrors the API's rules for the identification step. */
function validateSubmitter(who) {
  const found = {};
  const type = who.submitter_type;
  if (!type) {
    found.submitter_type = 'Say who is filling in this form.';
    return found;
  }
  if (!who.submitter_name.trim()) {
    found.submitter_name =
      type === 'agency' ? "Enter the contact person's name." : 'Enter your name.';
  }
  const email = who.submitter_email.trim();
  if (email && !/^\S+@\S+\.\S+$/.test(email)) {
    found.submitter_email = 'Enter a valid email address.';
  }
  const phone = who.submitter_phone.trim();
  if (phone && phone.replace(/\D/g, '').length < 10) {
    found.submitter_phone = 'Enter a valid contact number.';
  }
  if (type === 'creator' && !email && !who.submitter_profile.trim()) {
    found.submitter_profile = 'Enter your Instagram or YouTube handle, or your email.';
  }
  if (type === 'agency') {
    if (!who.submitter_agency.trim()) found.submitter_agency = "Enter the agency's name.";
    if (!email && !found.submitter_email) {
      found.submitter_email = "Enter the contact person's email.";
    }
  }
  if (type === 'internal' && !email && !found.submitter_email) {
    found.submitter_email = 'Enter your work email.';
  }
  return found;
}

/** Only the fields that apply to the chosen type are sent. */
function submitterPayload(who) {
  const keep =
    {
      creator: ['submitter_name', 'submitter_email', 'submitter_phone', 'submitter_profile'],
      agency: ['submitter_name', 'submitter_agency', 'submitter_email', 'submitter_phone'],
      internal: ['submitter_name', 'submitter_email', 'submitter_phone'],
    }[who.submitter_type] || [];
  const payload = { submitter_type: who.submitter_type };
  keep.forEach((key) => {
    payload[key] = who[key].trim();
  });
  return payload;
}

const EMPTY_INVOICE = {
  amount: '',
  invoice_number: '',
  contact_phone: '',
  contact_email: '',
  post_url: '',
};

function errorText(errors, key) {
  const value = errors && errors[key];
  if (!value) return '';
  return Array.isArray(value) ? value.join(' ') : String(value);
}

function Field({ label, name, errors, hint, full, children }) {
  const error = errorText(errors, name);
  return (
    <label className={`iv-field${full ? ' pub-full' : ''}`}>
      <span className="iv-label">{label}</span>
      {children}
      {error ? <span className="iv-hint iv-hint--error">{error}</span> : null}
      {!error && hint ? <span className="iv-hint">{hint}</span> : null}
    </label>
  );
}

export default function PublicInvoiceForm() {
  useStyleSheet('iv-kit', KIT_CSS);
  useStyleSheet('pf-fields', PAYOUT_CSS);
  useStyleSheet('pub-invoice', CSS);

  const { token } = useParams();
  const [link, setLink] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [values, setValues] = useState(EMPTY_INVOICE);
  const [payout, setPayout] = useState(EMPTY_PAYOUT);
  const [who, setWho] = useState(EMPTY_SUBMITTER);
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    getPublicInvoiceLink(token)
      .then((data) => {
        setLink(data);
        if (data && data.agreed_amount) {
          setValues((current) => ({ ...current, amount: String(data.agreed_amount) }));
        }
      })
      .catch((e) => setLoadError(e.message || 'This link is not valid.'));
  }, [token]);

  const set = (key) => (e) => {
    setValues({ ...values, [key]: e.target.value });
    if (errors[key]) setErrors({ ...errors, [key]: null });
  };

  const setWhoField = (key) => (e) => {
    const value = e.target.value;
    setWho((current) => ({ ...current, [key]: value }));
    if (errors[key]) setErrors((current) => ({ ...current, [key]: null }));
  };

  const pickType = (type) => {
    setWho((current) => ({ ...current, submitter_type: type }));
    if (errors.submitter_type) setErrors((current) => ({ ...current, submitter_type: null }));
  };

  const validate = () => {
    const found = {
      ...(link && link.viewer ? {} : validateSubmitter(who)),
      ...validatePayout(payout),
    };
    if (!values.amount || Number(values.amount) <= 0) {
      found.amount = 'Enter the amount being invoiced.';
    }
    if ((values.contact_phone || '').replace(/\D/g, '').length < 10) {
      found.contact_phone = 'Enter a valid contact number.';
    }
    if (!/^\S+@\S+\.\S+$/.test(values.contact_email || '')) {
      found.contact_email = 'Enter a valid email address.';
    }
    const postLink = (values.post_url || '').trim();
    if (postLink && !/^https?:\/\/\S+$/i.test(postLink)) {
      found.post_url = 'Enter the full link, starting with https://';
    }
    if (!file) found.invoice = 'Attach the invoice file.';
    return found;
  };

  const onSubmit = async () => {
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length) {
      setMessage('Some details need fixing — see the highlighted fields.');
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      const result = await submitPublicInvoice(
        token,
        {
          ...values,
          ...payoutPayload(payout),
          ...(link.viewer ? {} : submitterPayload(who)),
        },
        file,
      );
      setReceipt(result);
    } catch (e) {
      setErrors(e.fieldErrors || {});
      setMessage(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <div className="iv-root pub-page">
        <div className="pub-wrap">
          <p className="pub-brand">Influencer.in</p>
          <div className="pub-card">
            <Alert>{loadError}</Alert>
          </div>
        </div>
      </div>
    );
  }

  if (!link) {
    return (
      <div className="iv-root pub-page">
        <div className="pub-wrap">
          <div className="iv-sk" style={{ height: 160, borderRadius: 16 }} />
        </div>
      </div>
    );
  }

  if (receipt) {
    return (
      <div className="iv-root pub-page">
        <div className="pub-wrap">
          <p className="pub-brand">Influencer.in</p>
          <div className="pub-card pub-done">
            <div className="pub-done__icon">
              <Icon path={ICONS.check} size={24} />
            </div>
            <p className="pub-hero__title">Invoice received</p>
            <p className="pub-hero__meta">
              {money(receipt.amount)} for {receipt.campaign_name}. Our accounts team has it now and
              will be in touch at the email given if anything is missing.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const blocked = link.block_reason;

  return (
    <div className="iv-root pub-page">
      <div className="pub-wrap">
        <p className="pub-brand">Influencer.in</p>

        <div className="pub-hero">
          <p className="pub-hero__kicker">Invoice for</p>
          <h1 className="pub-hero__title">{link.campaign_name}</h1>
          <p className="pub-hero__meta">
            {link.brand_name ? `${link.brand_name} · ` : ''}
            {link.influencer_name}
          </p>
          <div className="pub-hero__amount">
            {link.agreed_amount ? (
              <span className="pub-hero__stat">
                <strong className="iv-num">{money(link.agreed_amount)}</strong>
                Agreed payout
              </span>
            ) : null}
            <span className="pub-hero__stat">
              <strong>{shortDate(link.expires_at)}</strong>
              Link valid until
            </span>
          </div>
        </div>

        {blocked ? (
          <div className="pub-card">
            <Alert>{blocked}</Alert>
          </div>
        ) : (
          <>
            {message ? (
              <div style={{ marginBottom: 18 }}>
                <Alert>{message}</Alert>
              </div>
            ) : null}

            <div className="pub-card">
              <p className="pub-card__title">Who is filling this in?</p>
              {link.viewer ? (
                <p className="pub-signed">
                  Signed in as <strong>{link.viewer.name || link.viewer.email}</strong>
                  {link.viewer.submitter_type === 'internal'
                    ? ' — recorded as the internal team, on the creator’s behalf.'
                    : ' — recorded as the creator.'}
                </p>
              ) : (
                <>
                  <p className="pub-card__sub">
                    Recorded with the invoice so our accounts team knows who to contact.
                  </p>
                  <div className="pub-who" role="radiogroup" aria-label="Who is filling this in">
                    {(link.submitter_types || DEFAULT_SUBMITTER_TYPES).map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={who.submitter_type === option.value}
                        className={`pub-who__opt${
                          who.submitter_type === option.value ? ' pub-who__opt--on' : ''
                        }`}
                        onClick={() => pickType(option.value)}
                      >
                        <span className="pub-who__label">{option.label}</span>
                        <span className="pub-who__hint">{SUBMITTER_HINTS[option.value]}</span>
                      </button>
                    ))}
                  </div>
                  {errorText(errors, 'submitter_type') ? (
                    <p className="pub-who__err">{errorText(errors, 'submitter_type')}</p>
                  ) : null}
                  {who.submitter_type ? (
                    <div className="pub-grid">
                      {who.submitter_type === 'agency' ? (
                        <Field label="Agency name" name="submitter_agency" errors={errors}>
                          <input
                            className={`iv-input${
                              errorText(errors, 'submitter_agency') ? ' iv-input--invalid' : ''
                            }`}
                            value={who.submitter_agency}
                            onChange={setWhoField('submitter_agency')}
                          />
                        </Field>
                      ) : null}
                      <Field
                        label={who.submitter_type === 'agency' ? 'Contact person' : 'Your name'}
                        name="submitter_name"
                        errors={errors}
                      >
                        <input
                          className={`iv-input${
                            errorText(errors, 'submitter_name') ? ' iv-input--invalid' : ''
                          }`}
                          value={who.submitter_name}
                          onChange={setWhoField('submitter_name')}
                        />
                      </Field>
                      <Field
                        label={who.submitter_type === 'internal' ? 'Work email' : 'Email'}
                        name="submitter_email"
                        errors={errors}
                        hint={who.submitter_type === 'creator' ? 'Or give your handle below.' : ''}
                      >
                        <input
                          className={`iv-input${
                            errorText(errors, 'submitter_email') ? ' iv-input--invalid' : ''
                          }`}
                          type="email"
                          value={who.submitter_email}
                          onChange={setWhoField('submitter_email')}
                        />
                      </Field>
                      <Field label="Phone" name="submitter_phone" errors={errors} hint="Optional.">
                        <input
                          className={`iv-input${
                            errorText(errors, 'submitter_phone') ? ' iv-input--invalid' : ''
                          }`}
                          type="tel"
                          value={who.submitter_phone}
                          onChange={setWhoField('submitter_phone')}
                        />
                      </Field>
                      {who.submitter_type === 'creator' ? (
                        <Field
                          label="Instagram or YouTube handle / profile link"
                          name="submitter_profile"
                          errors={errors}
                          full
                        >
                          <input
                            className={`iv-input${
                              errorText(errors, 'submitter_profile') ? ' iv-input--invalid' : ''
                            }`}
                            placeholder="@yourhandle or https://www.instagram.com/yourhandle"
                            value={who.submitter_profile}
                            onChange={setWhoField('submitter_profile')}
                          />
                        </Field>
                      ) : null}
                    </div>
                  ) : null}
                </>
              )}
            </div>

            <div className="pub-card">
              <p className="pub-card__title">Invoice</p>
              <p className="pub-card__sub">The invoice document and who to contact about it.</p>
              <div className="pub-grid">
                <Field label="Invoice amount (₹)" name="amount" errors={errors}>
                  <input
                    className={`iv-input iv-num${errorText(errors, 'amount') ? ' iv-input--invalid' : ''}`}
                    type="number"
                    min="0"
                    inputMode="numeric"
                    value={values.amount}
                    onChange={set('amount')}
                  />
                </Field>
                <Field label="Invoice number" name="invoice_number" errors={errors} hint="Optional.">
                  <input
                    className="iv-input"
                    value={values.invoice_number}
                    onChange={set('invoice_number')}
                  />
                </Field>
                <Field label="Contact number" name="contact_phone" errors={errors}>
                  <input
                    className={`iv-input${errorText(errors, 'contact_phone') ? ' iv-input--invalid' : ''}`}
                    type="tel"
                    value={values.contact_phone}
                    onChange={set('contact_phone')}
                  />
                </Field>
                <Field label="Contact email" name="contact_email" errors={errors}>
                  <input
                    className={`iv-input${errorText(errors, 'contact_email') ? ' iv-input--invalid' : ''}`}
                    type="email"
                    value={values.contact_email}
                    onChange={set('contact_email')}
                  />
                </Field>
                <Field
                  label="Post link"
                  name="post_url"
                  errors={errors}
                  hint="Link to the content this invoice is for."
                  full
                >
                  <input
                    className={`iv-input${errorText(errors, 'post_url') ? ' iv-input--invalid' : ''}`}
                    type="url"
                    placeholder="https://www.instagram.com/p/…"
                    value={values.post_url}
                    onChange={set('post_url')}
                  />
                </Field>
                <Field
                  label="Invoice document"
                  name="invoice"
                  errors={errors}
                  hint={file ? file.name : 'PDF, PNG or JPG, up to 10 MB.'}
                  full
                >
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={(e) => {
                      setFile(e.target.files && e.target.files[0]);
                      if (errors.invoice) setErrors({ ...errors, invoice: null });
                    }}
                  />
                </Field>
              </div>
            </div>

            <div className="pub-card">
              <p className="pub-card__title">Payout details</p>
              <p className="pub-card__sub">
                Where this invoice is paid. Used for this invoice only.
              </p>
              <PayoutFields
                values={payout}
                errors={errors}
                onChange={(key, value) => {
                  setPayout((current) => ({ ...current, [key]: value }));
                  if (errors[key]) setErrors((current) => ({ ...current, [key]: null }));
                }}
                disabled={saving}
              />
            </div>

            <div className="pub-foot">
              <button
                type="button"
                className="iv-btn iv-btn--primary"
                onClick={onSubmit}
                disabled={saving}
              >
                {saving ? <span className="iv-spin" /> : null}
                Submit invoice
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
